//  @flow

import React, { forwardRef } from 'react';
import { useCombobox } from 'downshift';
import classNames from 'classnames';
import Button from 'components/atoms/button/Button';
import { t } from 'javascripts/utils/withTranslation';
import type { InputType } from 'components/atoms/input/react/InputType';
import Input from '../../../atoms/input/react/Input';
import Label from '../../../atoms/label/react/Label';
import Icon from '../../../_particles/icon/react/Icon';
import type { ButtonProps } from '../../../atoms/button/Button';

export type Props = {
  ...InputType,
  suggestUrl: string,
  suggestFieldName?: string,
  suggestionItems?: string[],
  label?: string,
  error?: string,
  formProps?: {
    action?: string,
    method?: string,
  },
  hideSubmit?: boolean,
  tag?: string,
  value?: string,
  submitButtonProps?: Partial<ButtonProps>,
};

export type SearchResult = {
  suggestions: {
    [key: string]: number,
  },
  suggestion: string,
};

const fetchAutocomplete = async (url: string): Promise<string[]> => {
  try {
    const response = await fetch(url);
    const data: SearchResult = await response.json();
    return Object.keys(data?.suggestions || []) || [];
  } catch (error) {
    console.error('Error fetching autocomplete suggestions:', error);
    return [];
  }
};

const Search: React.ForwardRefRenderFunction<Props, HTMLInputElement> =
  forwardRef((props: Props, ref) => {
    const {
      suggestUrl,
      suggestFieldName = 'tx_solr[queryString]',
      placeholder,
      name = 'tx_solr[q]',
      label,
      suggestionItems,
      formProps,
      hideSubmit,
      tag: Tag = 'form',
      value: presetValue,
      submitButtonProps,
    } = props;
    const [inputItems, setInputItems] = React.useState<string[]>([]);
    const {
      isOpen,
      getToggleButtonProps,
      getLabelProps,
      getMenuProps,
      getInputProps,
      highlightedIndex,
      getItemProps,
      selectedItem,
      selectItem,
      reset,
      closeMenu,
      inputValue: value,
    } = useCombobox({
      initialInputValue: presetValue || '',
      items: inputItems,
      onInputValueChange: async ({ inputValue }) => {
        if (inputValue.length >= 3) {
          const items = await fetchAutocomplete(
            decodeURIComponent(
              `${suggestUrl}&${suggestFieldName}=${inputValue}`
            )
          );
          setInputItems(items);
        }
      },
    });

    const handleFocus = (e: any) => {
      e.target.select();
    };

    const handleClear = () => {
      reset();
      closeMenu();
    };

    /** Prevent Downshift's default 'Enter' behavior, when no item is highlighted. This is necessary to allow submitting the form via Enter when searching for something that is not in the suggestions. */
    const handleKeyDown = (event: any) => {
      if (event.key === 'Enter') {
        if (highlightedIndex === -1) {
          // eslint-disable-next-line no-param-reassign
          event.nativeEvent.preventDownshiftDefault = true;
        }
      }
    };

    const attr = Tag === 'form' ? { role: 'search', ...formProps } : {};

    return (
      <Tag className="search" {...attr}>
        <div className="search__inner">
          <div className="form-group form-group--large">
            <Label {...getLabelProps()}>{label}</Label>
            <div className="search__wrap">
              <Input
                {...getInputProps({
                  placeholder,
                  name,
                  onFocus: handleFocus,
                  onKeyDown: handleKeyDown,
                  ref,
                })}
                classNames="input--large"
              />
              {value && value.length > 0 && (
                <Button
                  icon="close"
                  style="icon"
                  onClick={handleClear}
                  className="button--large search__close"
                />
              )}
            </div>
            <div
              className={classNames(
                'search__dropdown',
                isOpen &&
                  inputItems.length > 0 &&
                  'dropdown-menu search__dropdown--open'
              )}
            >
              <ul className="dropdown-menu__list" {...getMenuProps()}>
                {isOpen &&
                  inputItems.length > 0 &&
                  inputItems.map((item, index) => {
                    const listItemClassName = classNames(
                      'dropdown-menu__list-item',
                      highlightedIndex === index &&
                        'dropdown-menu__list-item--active'
                    );
                    return (
                      <li
                        className={listItemClassName}
                        key={`${item}${index}`}
                        {...getItemProps({ item, index })}
                      >
                        <span className="dropdown-menu__link">
                          <span className="dropdown-menu__text">{item}</span>
                        </span>
                      </li>
                    );
                  })}
              </ul>
            </div>
          </div>

          {!hideSubmit && (
            <Button
              type="submit"
              icon="search"
              style="icon"
              iconTitle={
                submitButtonProps?.children ? undefined : t('Suche abschicken')
              }
              className="button--large search__submit"
              {...submitButtonProps}
            />
          )}
        </div>
        {suggestionItems && suggestionItems.length > 0 && (
          <div className="search__bottom">
            <h2 className="search__headline">{t('Top-Themen')}</h2>
            <ul className="search__list">
              {suggestionItems.map((item, index) => (
                <li className="search__list-item" key={`${item}${index}`}>
                  <a
                    className="button button--primary button--small"
                    onClick={() => selectItem(item)}
                    href={`${formProps?.action || '/'}?${name}=${item || ''}`}
                  >
                    <span className="button__text">{item}</span>
                  </a>
                </li>
              ))}
            </ul>
          </div>
        )}
      </Tag>
    );
  });

Search.displayName = 'Search';

export default Search;
